import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isOperator } from "auth/tier";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import type {
  DevOpsAttention,
  DevOpsOverview,
  DevOpsOverviewClient,
  DevOpsOverviewConnection,
  DevOpsOverviewMismatches,
  DevOpsOverviewProject,
} from "../devops.types";
import type DevOpsScopeStore from "../shared/DevOpsScopeStore";
import type DevOpsLinkDialogStore from "../shared/DevOpsLinkDialogStore";
import { subscribeDevOpsOverviewEvents } from "./devopsOverviewLive";
import {
  countAttention,
  groupProjectsByClient,
  projectMatchesSearch,
  type AttentionCounts,
  type DevOpsOverviewGroup,
  type DevOpsOverviewWindow,
} from "./devopsOverview.helpers";
import { isSessionEnded } from "stores/sessionGuard";
import { swallowedSessionEnded } from "shared/loadOrError";

/** How long after the last live `devops-event` the overview is re-found. */
export const LIVE_REFRESH_DEBOUNCE_MS = 2000;

export const DEFAULT_WINDOW_DAYS: DevOpsOverviewWindow = 7;

/** The Project 360 route a card click opens (Web-2 registers it). */
export const DEVOPS_PROJECT_ROUTE = "DevOpsProject";

export type DevOpsOverviewEmptyState =
  | "no-connections"
  | "no-projects"
  | "no-matches"
  | null;

/**
 * The `devops-overview` find() resolves the composite itself. Tolerate a
 * `{ data: [composite] }` envelope too, should the wrapper ever add one — but
 * never mistake an empty envelope for an overview.
 */
function unwrapOverview(result: unknown): DevOpsOverview | null {
  const isOverview = (v: unknown): v is DevOpsOverview =>
    !!v && typeof v === "object" && Array.isArray((v as DevOpsOverview).projects);
  if (isOverview(result)) return result;
  const data = (result as { data?: unknown } | null | undefined)?.data;
  if (Array.isArray(data) && isOverview(data[0])) return data[0];
  return null;
}

/**
 * Route ViewStore for the DevOps landing page — the client × project rollup.
 *
 * Plain class with `makeAutoObservable` (app store `devopsOverviewViewStore`).
 * Reads the find-only `devops-overview` ServiceStore and holds its RESPONSE
 * (`overview`) rather than reading the entity cache: the response is ONE
 * composite per call, keyed by nothing useful, and re-found on every window
 * change and live event — the same documented exception the DevOps boards use
 * for their Azure proxies (see `serviceStores/devops.stores.ts`).
 *
 * Filters are CLIENT-SIDE over that one response. The rollup is local data
 * and small (dozens of projects), so narrowing by client, search or attention
 * costs nothing and keeps the client select populated; only the activity
 * WINDOW is a server parameter, because the rollup is computed over it.
 *
 * LIVE: subscribes to every `devops-event` (no scope filter) and re-finds the
 * overview 2 s after the last one, so a burst of webhooks costs one call.
 * `initialize()` subscribes, `dispose()` unsubscribes and drops a pending
 * refresh — the route's onEnter/onExit.
 *
 * HONESTY: the library ServiceStore SWALLOWS a transport error (records
 * `errorOnFind`, resolves null), so a null find is an ERROR here, never "no
 * projects" — see {@link load}. And `no-activity` stays a neutral state all
 * the way to the card; nothing on this page calls a silent project healthy.
 */
class DevOpsOverviewStore {
  rootStore: RootStore;

  /** The last `devops-overview` response (see class doc). */
  overview: DevOpsOverview | null = null;

  clientFilter: string | null = null;
  windowDays: DevOpsOverviewWindow = DEFAULT_WINDOW_DAYS;
  search = "";
  attentionFilter: DevOpsAttention | null = null;

  loading = false;
  error: string | null = null;
  lastUpdatedAt: Date | null = null;

  private loadSeq = 0;
  private unsubscribeLive: (() => void) | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- Collaborators ------------------------------------------------------

  private get overviewService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore("devops-overview") as unknown as
        | ServiceStoreType
        | null
        | undefined) ?? null
    );
  }

  private get scopeStore(): DevOpsScopeStore | undefined {
    return this.rootStore.devopsScopeStore as DevOpsScopeStore | undefined;
  }

  private get linkDialog(): DevOpsLinkDialogStore | undefined {
    return this.rootStore.devopsLinkDialogStore as DevOpsLinkDialogStore | undefined;
  }

  // ---- Derived ------------------------------------------------------------

  /** Whether the user may link projects (operator+; the API re-checks). */
  get canManage(): boolean {
    return isOperator(this.rootStore);
  }

  get connections(): DevOpsOverviewConnection[] {
    return this.overview?.connections ?? [];
  }

  get projects(): DevOpsOverviewProject[] {
    return this.overview?.projects ?? [];
  }

  /** The header's client select, A→Z. */
  get clientOptions(): DevOpsOverviewClient[] {
    return (this.overview?.clients ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }

  get mismatches(): DevOpsOverviewMismatches | null {
    return this.overview?.mismatches ?? null;
  }

  /** Modules whose approval routing names a project HQ does not know. */
  get mismatchedModules(): DevOpsOverviewMismatches["modulesRoutingToUnknownProject"] {
    return this.mismatches?.modulesRoutingToUnknownProject ?? [];
  }

  /** Projects for the chosen client (all when none is chosen). */
  get scopedProjects(): DevOpsOverviewProject[] {
    const clientId = this.clientFilter;
    if (!clientId) return this.projects;
    return this.projects.filter((p) => p.clients.some((c) => c.id === clientId));
  }

  /**
   * The strip's numbers — counted over the CLIENT-scoped set, before search
   * and the attention filter, so each chip says what clicking it will show.
   */
  get attentionCounts(): AttentionCounts {
    return countAttention(this.scopedProjects);
  }

  /** What the cards show: client scope, then search, then the attention chip. */
  get visibleProjects(): DevOpsOverviewProject[] {
    let rows = this.scopedProjects;
    if (this.search.trim()) {
      const q = this.search;
      rows = rows.filter((p) => projectMatchesSearch(p, q));
    }
    if (this.attentionFilter) {
      const kind = this.attentionFilter;
      rows = rows.filter((p) => p.attention.includes(kind));
    }
    return rows;
  }

  /**
   * The visible projects grouped by client, "Unassigned to a client" last.
   * Under a client filter only THAT client's group renders — a project shared
   * with another client still shows both client chips on its card, but the
   * other client does not get a heading on a page scoped to this one.
   */
  get groups(): DevOpsOverviewGroup[] {
    // The response's CLIENT rows go in too: they carry the client's own
    // attention (`unattributed-activity`), which nothing else on this page
    // can see — the strip counts projects.
    const groups = groupProjectsByClient(this.visibleProjects, this.overview?.clients ?? []);
    const clientId = this.clientFilter;
    return clientId ? groups.filter((g) => g.id === clientId) : groups;
  }

  get hasActiveFilters(): boolean {
    return !!this.clientFilter || !!this.search.trim() || !!this.attentionFilter;
  }

  /**
   * Which honest empty state to render, or null when there are cards. Any
   * known project wins over "no connections" — an org whose connection row
   * was removed still has the history its events left.
   */
  get emptyState(): DevOpsOverviewEmptyState {
    if (!this.overview) return null;
    if (this.projects.length === 0) {
      return this.connections.length === 0 ? "no-connections" : "no-projects";
    }
    return this.visibleProjects.length === 0 ? "no-matches" : null;
  }

  connectionName(connectionId: string): string {
    return this.connections.find((c) => c.id === connectionId)?.name ?? "";
  }

  /**
   * The API's own caveat about the LIVE project list it decorated this
   * rollup with (`live.note`), rendered verbatim; nothing here infers a cause.
   * Null when the API sent none (including when `includeLive` was never
   * asked). WHAT the note is about is {@link livePartial} / {@link liveFetched}
   * — a note alone does not mean the list was truncated, because the API also
   * writes one when the list could not be read at all.
   */
  get liveNote(): string | null {
    return this.overview?.live?.note ?? null;
  }

  /**
   * Azure answered for EVERY connection asked. False with a note means at
   * least one connection could not be read (no PAT, a 403, an outage) — the
   * live list is missing, not short. False with no note means the live list
   * was never asked for, which is this page's normal state.
   */
  get liveFetched(): boolean {
    return this.overview?.live?.fetched === true;
  }

  /**
   * The live list is KNOWN to be incomplete: Azure's paging cap was hit with
   * more to come. This is the ONLY thing that licenses "a project may be
   * missing from this page" — never inferred from a note, because a failed
   * read carries a note too and is `partial: false`. Optional on the wire
   * (a hand-built fixture may omit it), so anything but `true` is false.
   */
  get livePartial(): boolean {
    return this.overview?.live?.partial === true;
  }

  // ---- Filters ------------------------------------------------------------

  setClientFilter(clientId: string | null) {
    this.clientFilter = clientId || null;
  }

  setSearch(query: string) {
    this.search = query;
  }

  /** Chip click: the same kind again clears it. */
  toggleAttention(kind: DevOpsAttention) {
    this.attentionFilter = this.attentionFilter === kind ? null : kind;
  }

  clearFilters() {
    this.clientFilter = null;
    this.search = "";
    this.attentionFilter = null;
  }

  /** The window is a SERVER parameter — a change re-finds the rollup. */
  async setWindow(days: DevOpsOverviewWindow) {
    if (days === this.windowDays) return;
    this.windowDays = days;
    await this.load();
  }

  // ---- Loading ------------------------------------------------------------

  /** Route onEnter (and the header refresh): subscribe once, then load. */
  async initialize() {
    this.startLive();
    await this.load();
  }

  async load() {
    const seq = ++this.loadSeq;
    this.loading = true;
    try {
      const service = this.overviewService;
      const result = await service?.find({ query: { windowDays: this.windowDays } });
      if (seq !== this.loadSeq) return; // a newer load superseded this one

      const overview = unwrapOverview(result);
      if (!overview) {
        // Not a failure if a sign-out ended the call. reset() bumps loadSeq, so
        // the check above already returns for that; this keeps the branch
        // right without leaning on it.
        if (swallowedSessionEnded(service)) return;
        // The wrapper swallowed the rejection: `errorOnFind` carries it. A null
        // here is a FAILED fetch, never an empty org — say so, keep the
        // previous overview (if any) so the body shows the stale-data warning.
        const findError = (service as { errorOnFind?: unknown } | null | undefined)
          ?.errorOnFind;
        runInAction(() => {
          this.error =
            findError instanceof Error
              ? findError.message
              : findError
                ? String((findError as { message?: unknown }).message ?? findError)
                : "Failed to load the DevOps overview";
        });
        console.warn("devops-overview load failed (swallowed by ServiceStore)");
        return;
      }

      runInAction(() => {
        this.overview = overview;
        this.error = null;
        this.lastUpdatedAt = new Date();
      });
    } catch (err: unknown) {
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = err instanceof Error ? err.message : "Failed to load the DevOps overview";
      });
      console.error("Failed to load the DevOps overview:", err);
    } finally {
      runInAction(() => {
        if (seq === this.loadSeq) this.loading = false;
      });
    }
  }

  /** Re-find the rollup (header refresh, a saved link, a live event). */
  async refresh() {
    await this.load();
  }

  // ---- Navigation / actions ----------------------------------------------

  /**
   * Card click. A project WITH an id: set THE shared DevOps selection so
   * Project 360 and every board are already scoped, then go there. (The name
   * may be null for an id-only project — the boards fill it in from the live
   * list; the scope never holds a placeholder as a name.)
   *
   * A NAME-ONLY project (`projectId === null` — HQ has only seen its name in
   * webhooks) has no page identity: the Project 360 route cannot resolve a
   * null id, and writing a null project into the shared scope would clear
   * every board's project. So the click neither touches the scope nor
   * navigates; it opens the shared Link dialog seeded for that connection
   * and name (the dialog matches the name against the live project list).
   * Linking it is what gives it an id — and a page.
   */
  openProject(project: DevOpsOverviewProject) {
    if (project.projectId === null) {
      this.linkDialog?.open({
        preselect: {
          connectionId: project.connectionId,
          projectName: project.name ?? undefined,
        },
        onSaved: () => void this.refresh(),
      });
      return;
    }
    this.scopeStore?.setScope({
      connectionId: project.connectionId,
      projectId: project.projectId,
      projectName: project.name,
    });
    void this.rootStore.routerStore?.goTo(DEVOPS_PROJECT_ROUTE, {
      connectionId: project.connectionId,
      projectId: project.projectId,
    });
  }

  /**
   * Seed the shared "Link a project" dialog; the header's `DialogButton`
   * opens it after this runs. A saved link re-finds the overview.
   */
  startLink() {
    this.linkDialog?.begin({ onSaved: () => void this.refresh() });
  }

  // ---- Live ---------------------------------------------------------------

  /** Subscribe to every live `devops-event`. Idempotent. */
  startLive() {
    if (this.unsubscribeLive) return;
    this.unsubscribeLive = subscribeDevOpsOverviewEvents(this.rootStore, () =>
      this.scheduleRefresh()
    );
  }

  /** Coalesce a burst of events into one re-find, 2 s after the last. */
  scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      // The event loop calls this, so it is outside the action MobX wraps
      // around a method — clearing the handle needs its own action.
      runInAction(() => {
        this.refreshTimer = null;
      });
      void this.refresh();
    }, LIVE_REFRESH_DEBOUNCE_MS);
  }

  /** Route onExit — tear down the subscription and any pending refresh. */
  dispose() {
    if (this.unsubscribeLive) {
      this.unsubscribeLive();
      this.unsubscribeLive = null;
    }
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.overview = null;
    this.clientFilter = null;
    this.windowDays = DEFAULT_WINDOW_DAYS;
    this.search = "";
    this.attentionFilter = null;
    this.loading = false;
    this.error = null;
    this.lastUpdatedAt = null;
    this.loadSeq += 1;
    this.unsubscribeLive = null;
    this.refreshTimer = null;
  }
}

export default DevOpsOverviewStore;
